/**
 * `@dxf-ai-editor/drawing-model` — the ONE-TRUTH answer to "which VERTEX
 * records of a SMOOTHED polygon mesh are the surface AutoCAD draws, and which
 * are the control frame it does not" (@codex PR #785 round 18, P2).
 *
 * # The DXF fact
 *
 * `PEDIT ▸ Smooth` on a polygon mesh does not merely tag the entity: it
 * **generates and stores** a second, denser grid of vertices lying ON the
 * fitted surface, and demotes the authored 71×72 grid to a control frame.
 *
 *   - ODA `DbPolygonMesh.h` annotates every accessor with its DXF group and
 *     says of 73 / 74 verbatim: *"This is the number of vertices in the M [N]
 *     direction **after a surfaceFit**. Used **instead of M [N]** if
 *     `polyMeshType() != OdDb::kSimpleMesh`"*, where `polyMeshType()` IS group
 *     75 (`kSimpleMesh` 0, `kQuadSurfaceMesh` 5, `kCubicSurfaceMesh` 6,
 *     `kBezierSurfaceMesh` 8). Its `straighten()` is documented as *"Removes
 *     all the surface fit vertices"* — one cannot remove vertices that were
 *     never stored.
 *   - the same header maps the vertex kinds onto DXF group 70 EXACTLY:
 *     `k3dSimpleVertex` → `0x40`, `k3dControlVertex` → `0x10` ("Spline-fit or
 *     curve-fit control point"), `k3dFitVertex` → `0x08` ("Spline-fit or
 *     curve-fit **generated** vertex") — this codebase's
 *     {@link DXF_VERTEX_FLAG.POLYGON_MESH_VERTEX},
 *     `SPLINE_FRAME_CONTROL_POINT` and `SPLINE_VERTEX`.
 *   - measured on a real file: a 3×4 control net smoothed to a cubic B-spline
 *     surface at densities 5×6 stores `70 = 20` (POLYGON_MESH | SPLINE_FIT),
 *     `71 = 3  72 = 4  73 = 5  74 = 6  75 = 6` and **42** VERTEX records — 30
 *     with `70 = 72` (`0x40 | 0x08`, the fit surface) followed by 12 with
 *     `70 = 80` (`0x40 | 0x10`, the retained control net).
 *
 * AutoCAD draws the generated surface; the control net is the frame `SPLFRAME`
 * shows only when it is ON. It is the SAME storage shape the 2D spline-fit
 * polyline already uses (generated bit-8 vertices first, bit-16 frame after),
 * which `cad-core`'s `heavyPolylineTopology.ts::heavyPolylineVisibleIndices`
 * has filtered on since round 3.
 *
 * # Why this rule is sited HERE and not in `cad-core`
 *
 * Exactly the reason `./polylineWidth.ts`' header gives for the authored-width
 * rule, and it is now load-bearing rather than precautionary. `cad-core`
 * depends on `drawing-model` and never the reverse, so a rule sited in
 * `cad-core` is UNREACHABLE from this package's own WCS bounds walk
 * (`./wcsBoundsLeaf.ts::heavyPolylineLocal`) — and that walk is precisely the
 * consumer that was still bounding every record of a fitted mesh while
 * `cad-core`'s `polylineMeshTopology.ts` already drew and picked only the
 * generated pool. A control point parked far outside the surface it generates
 * therefore stayed in the document extents, in MVIEW FIT and in viewport
 * membership although nothing draws it.
 *
 * Sited here, the render/pick topology (`cad-core`) and the bounds walk (this
 * package) read the SAME predicate and the SAME pool, with no layering
 * inversion available to a later caller and no second copy of the bit
 * arithmetic for `dup:check` to flag. The GRID dimensioning (73 × 74 row-major
 * rows and columns) stays in `cad-core`'s `polylineMeshTopology.ts`, which is
 * the only consumer that needs a wireframe rather than a point set.
 */

import { DXF_VERTEX_FLAG } from "@dxf-ai-editor/dxf-io";

/**
 * The minimum a caller must supply: group 75 and each record's own group-70
 * flags.
 *
 * Structural rather than `PolylineEntity` for the same reason
 * `cad-core`'s `PolylineMeshTopologySource` is — the AI `osnap-query` route
 * reads a SNAPSHOT FACET that copies both channels verbatim but is not the
 * entity. `PolylineEntity` satisfies this shape as-is.
 */
export interface PolylineSurfaceFitSource {
  readonly vertices: readonly { readonly flags?: number | undefined }[];
  /**
   * 75 — curves and smooth surface type: 0 none, 5 quadratic B-spline,
   * 6 cubic B-spline, 8 Bezier surface.
   */
  readonly surfaceType?: number | undefined;
}

/**
 * Whether group 75 declares a FITTED surface: 5 `kQuadSurfaceMesh`,
 * 6 `kCubicSurfaceMesh` and 8 `kBezierSurfaceMesh` (ODA `OdDb::PolyMeshType`).
 * 0 `kSimpleMesh` — and any value a producer invents — is not one.
 *
 * ## Why a PREDICATE and not the `readonly number[]` this began as
 *
 * Not style: the targeted mutation gate measured that the array form was
 * UNTESTABLE-IN-PRINCIPLE by this repo's runner. A module-level array literal
 * is a STATIC mutant (`"static": true` in `reports/mutation/mutation.json`) —
 * its code runs ONCE, at module load, before Stryker activates any mutant —
 * and Stryker's `vitest` runner reuses the loaded module graph across
 * per-mutant runs, so the mutation is never actually in effect. `[5, 6, 8] →
 * []` was therefore reported `Survived` with `"coveredBy": []`: the gate
 * declared the ENTIRE surface-fit discriminator unprotected. Three `===`
 * comparisons in a function body express the identical contract as ordinary
 * NON-static mutants, every one of which the suites kill.
 */
export function isSmoothSurfaceType(surfaceType: number): boolean {
  return surfaceType === 5 || surfaceType === 6 || surfaceType === 8;
}

/**
 * The indices of the GENERATED (group-70 bit 8) surface records of a FITTED
 * polygon mesh, in file order — or `null` when the entity is not one.
 *
 * `null` (rather than an empty list) for BOTH halves of the gate, and the
 * caller must then keep every record:
 *
 *   - group 75 does not declare a fitted surface — the overwhelmingly common
 *     `75 = 0` simple mesh, whose records are all authored geometry;
 *   - group 75 declares one but NO record carries bit 8 — a producer that
 *     wrote the type without generating the surface (`SPLINEDIT`'s mesh
 *     analogue). AutoCAD still draws that entity's frame rather than making it
 *     vanish, so the filter must not empty it out.
 *
 * Two-part exactly like `cad-core`'s `heavyPolylineVisibleIndices`: group 75 is
 * the entity-level discriminator ODA names and cannot be triggered by a stray
 * per-record bit, while the pool test is what proves the surface was really
 * stored.
 *
 * The caller decides WHICH subtype this applies to. `wcsBoundsLeaf.ts` and
 * `cad-core`'s `polylineMeshTopology.ts` both gate it on
 * `polylineSubtype(entity) === "polygon-mesh"`, so a poly-face mesh's vertex
 * POOL (whose own filter is the FACE-record bit decode) is never touched by
 * this rule.
 */
export function surfaceFitVertexIndices(
  entity: PolylineSurfaceFitSource,
): number[] | null {
  if (!isSmoothSurfaceType(entity.surfaceType ?? 0)) return null;
  const pool: number[] = [];
  for (let i = 0; i < entity.vertices.length; i += 1) {
    if (((entity.vertices[i]!.flags ?? 0) & DXF_VERTEX_FLAG.SPLINE_VERTEX) !== 0) {
      pool.push(i);
    }
  }
  return pool.length === 0 ? null : pool;
}
